import { useEffect } from 'react';
import {
  usePropertiesAPI,
  UsePropertiesAPIReturn,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';
import {
  useDocumentsAPI,
  UseFaqsNotesDocumentsAPIReturn,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/useDocumentsAPI';
import {
  useServiceRequestFormOptions,
  UseServiceRequestFormOptionsReturn,
} from 'src/components/ServiceRequestForm/CustomHooks/useServiceRequestFormOptions';
import { Property, Suite } from 'src/SharedCustomHooks/UseSuiteActions/types';

export interface UseServiceRequestReferenceDataReturn {
  issueTypes: any[];
  serviceRequestTags: any[];
  catalogItems: any[];
  isLoadingFormOptions: boolean;
  formOptionsError: string | null;

  properties: Property[];
  isLoadingProperties: boolean;

  propertySuites: Suite[];
  isLoadingPropertySuites: boolean;
  loadPropertySuites: (propertyId: string) => Promise<void>;

  buildings: any[];
  loadPropertyBuildings: (propertyId: string) => Promise<void>;
  getBuildingName: (buildingId?: string) => string;

  faqs: any[];
  isLoadingFaqs: boolean;
  loadFaqsForAsset: (assetId: string) => Promise<void>;
  getFaqsForIssueType: (issueType?: string) => any[];
}

export const useServiceRequestReferenceData = (
  user: any
): UseServiceRequestReferenceDataReturn => {
  const userId: string | undefined = user?.id;
  const role: string | undefined = user?.roles?.[0]?.role?.name;

  const {
    types,
    serviceRequestTags,
    items,
    loading: isLoadingFormOptions,
    error: formOptionsError,
    fetchFormOptions,
  }: UseServiceRequestFormOptionsReturn = useServiceRequestFormOptions();

  const {
    properties,
    isLoadingProperties,
    fetchProperties,
    propertySuites,
    isLoadingPropertySuites,
    fetchPropertySuites,
    buildings,
    fetchBuildingForProperty,
  }: UsePropertiesAPIReturn = usePropertiesAPI();

  const {
    faqs,
    loadingDocs: isLoadingFaqs,
    fetchDocuments,
  }: UseFaqsNotesDocumentsAPIReturn = useDocumentsAPI({ user });

  useEffect(() => {
    if (!userId || !role) {
      return;
    }

    fetchProperties(userId, role);
    fetchFormOptions(userId, role);
  }, [userId, role]);

  const loadPropertySuites = async (propertyId: string): Promise<void> => {
    if (!userId || !role) {
      return;
    }

    await fetchPropertySuites(propertyId, userId, role);
  };

  const loadPropertyBuildings = async (propertyId: string): Promise<void> => {
    await fetchBuildingForProperty(propertyId);
  };

  const getBuildingName = (buildingId?: string): string => {
    if (!buildingId) {
      return '';
    }

    const matchedBuilding: any = buildings.find(
      (building: any) => building.id === buildingId
    );

    return matchedBuilding?.building ?? '';
  };

  const loadFaqsForAsset = async (assetId: string): Promise<void> => {
    await fetchDocuments(assetId);
  };

  const getFaqsForIssueType = (issueType?: string): any[] => {
    return faqs.filter((faq: any) => {
      if (typeof faq.type === 'object') {
        return faq.type.includes(issueType);
      }

      return faq.type === issueType;
    });
  };

  return {
    issueTypes: types,
    serviceRequestTags,
    catalogItems: items,
    isLoadingFormOptions,
    formOptionsError,

    properties,
    isLoadingProperties,

    propertySuites,
    isLoadingPropertySuites,
    loadPropertySuites,

    buildings,
    loadPropertyBuildings,
    getBuildingName,

    faqs,
    isLoadingFaqs,
    loadFaqsForAsset,
    getFaqsForIssueType,
  };
};
